import { AgGridCustomFilterAdapter } from '@components/CustomTableFilter';
import type { CatalogRatioHistoryData } from '@gql/graphql';
import { formatDateToLocale, formatNumberToRu } from '@utils';
import { historyGetIsChangedStyle } from '@utils/historyGetIsChangedStyle';

import type { ColDef, ColGroupDef } from 'ag-grid-community';
import { useCallback, useMemo } from 'react';

import Styles from '../../../catalogRatios.module.css';
import { ColumnPairs, RatiosTypeEnum } from '../../../catalogRatiosHelpers';
import {
    catalogRatiosHistoryCodeFilterGql,
    catalogRatiosHistoryGroupFilterGql,
    catalogRatiosHistoryJustificationFilterGql,
    catalogRatiosHistoryNameFilterGql,
    catalogRatiosHistoryTypeFilterGql,
} from '../catalogRatiosHistoryQueries';
import { historyFilterColumns } from '../helpers';

type TUseHistoryColumnsProps = {
    handlerFilterChange: ({
        filterType,
        value,
    }: {
        filterType: 'columnFilters';
        value: Record<string, any[]> | null;
    }) => void;
    catalogId: string | undefined;
    tableRdy: boolean;
    dateFilterValues: {
        endDate: Date | undefined;
        startDate: Date | undefined;
    };
};

export const useHistoryColumns = ({
    catalogId,
    handlerFilterChange,
    tableRdy,
    dateFilterValues,
}: TUseHistoryColumnsProps): (ColDef<CatalogRatioHistoryData> | ColGroupDef<CatalogRatioHistoryData>)[] => {
    const filterVariables = useMemo(
        () => ({
            catalogUniqueId: catalogId,
            startDate: dateFilterValues.startDate,
            endDate: dateFilterValues.endDate,
        }),
        [catalogId, dateFilterValues.endDate, dateFilterValues.startDate],
    );

    const onFilterChange = useCallback(
        ({ fieldName, checkedValues }: { fieldName: string; checkedValues: any[] }) => {
            handlerFilterChange({ filterType: 'columnFilters', value: { [fieldName]: checkedValues } });
        },
        [handlerFilterChange],
    );

    return [
        {
            headerName: 'Дата изменения',
            field: 'changeDate',
            width: 150,
            valueGetter: ({ data }: { data: CatalogRatioHistoryData | undefined }) =>
                data?.isHeadLine ? data?.code : formatDateToLocale(data?.changeDate),
            colSpan: ({ data }: { data: CatalogRatioHistoryData | undefined }) => (data?.isHeadLine ? 6 : 1),
        },
        {
            headerName: ColumnPairs.groupName,
            field: historyFilterColumns.groupName,
            flex: 1,
            minWidth: 200,
            ...(tableRdy && {
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables: filterVariables,
                    changeFilterHandler: onFilterChange,
                    dataAccessor: 'catalogRatioHistoryGroupFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: ColumnPairs.groupName,
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    query: catalogRatiosHistoryGroupFilterGql,
                },
            }),
        },
        {
            headerName: ColumnPairs.code,
            field: historyFilterColumns.code,
            minWidth: 160,
            ...(tableRdy && {
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables: filterVariables,
                    changeFilterHandler: onFilterChange,
                    dataAccessor: 'catalogRatioHistoryCodeFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: ColumnPairs.code,
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    query: catalogRatiosHistoryCodeFilterGql,
                },
            }),
        },
        {
            headerName: ColumnPairs.name,
            field: historyFilterColumns.name,
            minWidth: 400,
            cellClass: historyGetIsChangedStyle(),
            ...(tableRdy && {
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables: filterVariables,
                    changeFilterHandler: onFilterChange,
                    dataAccessor: 'catalogRatioHistoryNameFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: ColumnPairs.name,
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    query: catalogRatiosHistoryNameFilterGql,
                },
            }),
        },
        {
            headerName: ColumnPairs.ratioType,
            field: historyFilterColumns.ratioType,
            minWidth: 190,
            valueFormatter: ({ value }: { value: keyof typeof RatiosTypeEnum }) => RatiosTypeEnum[value],
            ...(tableRdy && {
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    withSearch: false,
                    variables: filterVariables,
                    changeFilterHandler: onFilterChange,
                    dataAccessor: 'catalogRatioHistoryTypeFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: ColumnPairs.ratioType,
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                            valueFormatter: ({ value }: { value: keyof typeof RatiosTypeEnum }) =>
                                RatiosTypeEnum[value],
                        },
                    ],
                    query: catalogRatiosHistoryTypeFilterGql,
                },
            }),
        },
        {
            field: 'ratios' as any,
            headerName: ColumnPairs.ratios,
            children: [
                {
                    field: historyFilterColumns.kozp,
                    headerName: ColumnPairs.kozp,
                    flex: 1,
                    minWidth: 152,
                    cellClass: historyGetIsChangedStyle(),
                    cellRenderer: ({ value }: { value: string }) => (
                        <div className={Styles.ratioCell}>{formatNumberToRu(value, 4) ?? ''}</div>
                    ),
                },
                {
                    field: historyFilterColumns.kem,
                    headerName: ColumnPairs.kem,
                    flex: 1,
                    minWidth: 152,
                    cellClass: historyGetIsChangedStyle(),
                    cellRenderer: ({ value }: { value: string }) => (
                        <div className={Styles.ratioCell}>{formatNumberToRu(value, 4) ?? ''}</div>
                    ),
                },
                {
                    field: historyFilterColumns.knr,
                    headerName: ColumnPairs.knr,
                    flex: 1,
                    minWidth: 152,
                    cellClass: historyGetIsChangedStyle(),
                    cellRenderer: ({ value }: { value: string }) => (
                        <div className={Styles.ratioCell}>{formatNumberToRu(value, 4) ?? ''}</div>
                    ),
                },
                {
                    field: historyFilterColumns.ksp,
                    headerName: ColumnPairs.ksp,
                    flex: 1,
                    minWidth: 152,
                    cellClass: historyGetIsChangedStyle(),
                    cellRenderer: ({ value }: { value: string }) => (
                        <div className={Styles.ratioCell}>{formatNumberToRu(value, 4) ?? ''}</div>
                    ),
                },
                {
                    field: historyFilterColumns.kmat,
                    headerName: ColumnPairs.kmat,
                    flex: 1,
                    minWidth: 152,
                    cellClass: historyGetIsChangedStyle(),
                    cellRenderer: ({ value }: { value: string }) => (
                        <div className={Styles.ratioCell}>{formatNumberToRu(value, 4) ?? ''}</div>
                    ),
                },
                {
                    field: historyFilterColumns.ktz,
                    headerName: ColumnPairs.ktz,
                    flex: 1,
                    minWidth: 152,
                    cellClass: historyGetIsChangedStyle(),
                    cellRenderer: ({ value }: { value: string }) => (
                        <div className={Styles.ratioCell}>{formatNumberToRu(value, 4) ?? ''}</div>
                    ),
                },
                {
                    field: historyFilterColumns.ktzzpm,
                    headerName: ColumnPairs.ktzzpm,
                    flex: 1,
                    minWidth: 152,
                    cellClass: historyGetIsChangedStyle(),
                    cellRenderer: ({ value }: { value: string }) => (
                        <div className={Styles.ratioCell}>{formatNumberToRu(value, 4) ?? ''}</div>
                    ),
                },
            ],
        },
        {
            headerName: ColumnPairs.justification,
            field: historyFilterColumns.justification,
            minWidth: 210,
            ...(tableRdy && {
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables: filterVariables,
                    changeFilterHandler: onFilterChange,
                    dataAccessor: 'catalogRatioHistoryJustificationFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: ColumnPairs.justification,
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    query: catalogRatiosHistoryJustificationFilterGql,
                },
            }),
        },
    ];
};
